import { hasAnalyticsConsent } from "@/lib/consent";

const WEBHOOK_URL =
  process.env.REACT_APP_ANALYTICS_WEBHOOK_URL ||
  "https://ck-partners.app.n8n.cloud/webhook/site-track-v2";

const SID_COOKIE = "ka_sid";
const SID_MAX_AGE_DAYS = 180;

function readCookie(name) {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days) {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

// Persistent id, only created once analytics consent is granted.
function getSessionId() {
  let id = readCookie(SID_COOKIE);
  if (!id) {
    id = crypto.randomUUID();
    writeCookie(SID_COOKIE, id, SID_MAX_AGE_DAYS);
  }
  return id;
}

function getUtm() {
  const stored = sessionStorage.getItem("_ka_utm");
  if (stored) return JSON.parse(stored);
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utmSource: params.get("utm_source") || "",
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
  };
  sessionStorage.setItem("_ka_utm", JSON.stringify(utm));
  return utm;
}

function send(eventType, extra = {}) {
  if (!hasAnalyticsConsent()) return;
  const payload = {
    sessionId: getSessionId(),
    eventType,
    path: window.location.pathname,
    referrer: sessionStorage.getItem("_ka_ref") ?? document.referrer,
    ...getUtm(),
    scrollDepth: 0,
    dwellSeconds: 0,
    ...extra,
  };
  const body = JSON.stringify(payload);
  // text/plain is a CORS-safelisted content type: the browser sends this as a
  // "simple request" without a preflight OPTIONS call. application/json would
  // require a preflight, which our webhook doesn't answer, so the browser
  // silently drops the request before it ever reaches n8n.
  if (navigator.sendBeacon) {
    navigator.sendBeacon(WEBHOOK_URL, new Blob([body], { type: "text/plain" }));
  } else {
    fetch(WEBHOOK_URL, { method: "POST", headers: { "Content-Type": "text/plain" }, body, keepalive: true }).catch(() => {});
  }
}

export function trackPageview() {
  if (!sessionStorage.getItem("_ka_ref")) {
    sessionStorage.setItem("_ka_ref", document.referrer);
  }
  send("pageview");
}

export function trackFormEvent(eventType) {
  send(eventType);
}

const scrollMilestonesHit = new Set();
let scrollListenerAttached = false;

export function initScrollTracking() {
  if (scrollListenerAttached) return;
  scrollListenerAttached = true;
  const milestones = [25, 50, 75, 100];
  const onScroll = () => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - doc.clientHeight;
    if (scrollable <= 0) return;
    const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
    milestones.forEach((m) => {
      if (pct >= m && !scrollMilestonesHit.has(m)) {
        scrollMilestonesHit.add(m);
        send("scroll_depth", { scrollDepth: m });
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
}

let dwellListenerAttached = false;

export function initDwellTracking() {
  if (dwellListenerAttached) return;
  dwellListenerAttached = true;
  const start = Date.now();
  const flush = () => {
    const dwellSeconds = Math.round((Date.now() - start) / 1000);
    if (dwellSeconds > 0) send("dwell", { dwellSeconds });
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}
